import basketStore from '@shared/store/basket'
import partnerStore from '@shared/store/partner'
import sessionStore from '@shared/store/session'
import useBasket from '@shared/hooks/useBasket'
import useI18n from '@shared/hooks/useI18n'

import { createDistributionPartners } from '@shared/helpers/Partner'

import { CHANNEL, ROUTE_NAME, SALES_PARTNER_TYPE } from '@shared/config/constants'

const isTestEnv = import.meta.env.VITE_ENV_KEY !== 'PRD'

// HOOKS
const { createDefaultBasket } = useBasket()
const { selectedLanguage } = useI18n()

/**
 * Add all partner routes
 *
 * partner
 * PARTNER (Smile-Direct): #/partner/346448
 * PRIVATE_INSURER (Allianz): #/partner/317826
 * BROKER: #/partner/337981
 * MOBILE: #/partner/365231
 *
 * collective-partner
 * FH Schweiz: #/partner/K003910
 * Credit Suisse: #/partner/K003000
 * Siemens Kader: #/partner/K003777
 * Finanzverwaltung Basel Stadt: #/partner/K004070
 */
export default [
  {
    name: ROUTE_NAME.PARTNER,
    path: 'partner/:distributionPartnerNumber([0-9]{6})',
    meta: {
      secured: false,
    },
    async beforeEnter(to) {
      const distributionPartnerNumber = to.params.distributionPartnerNumber

      try {
        await sessionStore.clearSession()
      } catch {
        // No session yet, just proceed
      }
      basketStore.emptyBasket()

      const payload = await createDefaultBasket({
        channel: CHANNEL.PARTNERDVP,
        distributionPartners: createDistributionPartners(distributionPartnerNumber),
        partnerNumber: distributionPartnerNumber,
        language: selectedLanguage.value,
      })

      const partnerData = await partnerStore.install({
        contractStartDate: payload.contractStartDate,
        language: selectedLanguage.value,
        collectiveNumber: null,
        partnerNumber: null,
        distributionPartnerNumber,
      })

      const validPartnerTypes = [
        SALES_PARTNER_TYPE.DEFAULT,
        SALES_PARTNER_TYPE.DEFAULT_SANITAS,
        SALES_PARTNER_TYPE.PARTNER,
      ]

      /**
       * @NOTE: only default, default_sanitas and partner are allowed to use this entry
       */
      if (validPartnerTypes.includes(partnerData.distributionPartnerInfo.salesPartnerType) || isTestEnv) {
        if (partnerData.distributionPartnerInfo.masterTreaty) {
          payload.masterTreaty = partnerData.distributionPartnerInfo.masterTreaty
        }

        if (
          [SALES_PARTNER_TYPE.BROKER, SALES_PARTNER_TYPE.PRIVATE_INSURER].includes(
            partnerData.distributionPartnerInfo.salesPartnerType
          )
        ) {
          payload.salesPartnerEmail = partnerData.distributionPartnerInfo.email
        }

        // await basketStore.updateOvpBasket(payload)
        await sessionStore.setSession(payload, { hot: false, preventCookie: true })
        await basketStore.persistBasket(payload)

        return
      } else {
        return {
          name: ROUTE_NAME.PERSONS,
          params: {
            lang: selectedLanguage.value,
          }
        }
      }
    },
    component: () => import('@shared/modules/Dvp/screens/Persons/ScreenPersons'),
  },
  {
    name: ROUTE_NAME.COLLECTIVE,
    path: 'partner/:collectiveNumber(K[0-9]{6})',
    meta: {
      secured: false,
    },
    async beforeEnter(to, _from) {
      const collectiveNumber = to.params.collectiveNumber

      try {
        await sessionStore.clearSession()
      } catch {
        // No session yet, just proceed
      }
      basketStore.emptyBasket()

      const payload = await createDefaultBasket({
        channel: CHANNEL.PARTNERDVP,
        language: selectedLanguage.value,
      })
      // const config = await install(payload, collectiveNumber)

      const partnerData = await partnerStore.install({
        contractStartDate: payload.contractStartDate,
        language: selectedLanguage.value,
        collectiveNumber,
        partnerNumber: null,
        distributionPartnerNumber: null,
      })

      const cConfig = partnerData.collectiveContractConfig

      payload.distributionPartners = createDistributionPartners(cConfig.distributionPartnerSanitas)

      // ['infoText'] not an allowed key for the collective-property
      delete cConfig.infoText

      // @note: isn't here only channel and contractNumber needed?
      payload.collective = cConfig

      if (cConfig.masterTreaty) {
        payload.masterTreaty = cConfig.masterTreaty
      }

      // @note: can't create a hot basket here, as it would be created without persons, which would screw the webWidgets
      // await basketStore.updateOvpBasket(payload)
      await sessionStore.setSession(payload, { hot: false, preventCookie: true })
      await basketStore.persistBasket(payload)

      return

      // next(getNextRoute(to))
    },
    component: () => import('@shared/modules/Dvp/screens/Persons/ScreenPersons'),
  },
]
